<template>
    <q-expansion-item
        expand-icon-toggle
    >
        <template #header>
            <q-item-section side>
                <q-btn
                    dense
                    :outline="$q.dark.isActive"
                    size="sm"
                    :flat="!$q.dark.isActive"
                    icon="mdi-delete"
                    color="negative"
                    @click="$emit('delete', pay.worker)"
                />
            </q-item-section>
            <q-item-section>
                <q-item-label>
                    <Avatar size="sm" :user="pay.worker" :contact="false" class="q-mr-sm" />
                    <span class="text-underline cursor-pointer">
                        {{ pay.worker.name ?? 'N/A' }}
                        <q-popup-edit 
                            auto-save
                            :model-value="pay.split" 
                            @update:model-value="$event => handleInput($event)"
                        >
                            <template v-slot="scope">
                                <q-item dense>
                                    <q-item-section>
                                        <q-item-label class="text-center">
                                            <Avatar size="sm" :user="pay.worker" :contact="false" />
                                            {{ pay.worker.name ?? 'N/A' }}
                                        </q-item-label>
                                    </q-item-section>
                                </q-item>
                                <q-input
                                    label="Worker Split"
                                    v-model="scope.value"
                                    dense
                                    autofocus
                                    clearable
                                    @clear="scope.value = 0"
                                    @keyup.enter="scope.set"
                                    type="number"
                                    :rules="[val => val <= fixedFloat(parseFloat(pay.split) + parseFloat(remaining)) || 'Worker split cannot be greater than ' + parseFloat(pay.split + remaining).toFixed(2) + '%', val => val >= 0 || 'Worker split cannot be less than 0%']"
                                    :hint="`Remaining: ${parseFloat(pay.split - scope.value).toFixed(2)}% | Max: ${parseFloat(parseFloat(pay.split) + parseFloat(remaining)).toFixed(2)}%`"
                                >
                                    <template #append>
                                        <q-icon name="mdi-percent" />
                                    </template>
                                    <template #after>
                                        <q-btn
                                            padding="5px 7px"
                                            icon="mdi-decimal"
                                            color="primary"
                                            @click="scope.value = fixedFloat((scope.value + '').split('.')[0] + '.00')"
                                        >
                                            <q-tooltip>
                                                Zero out decimal
                                            </q-tooltip>
                                        </q-btn>
                                    </template>
                                </q-input>
                                
                                <q-item>
                                    <q-item-section v-for="i in ['-.01', '-.1', '-1', '1', '.1', '.01']">
                                        <!-- -1, -0.1, -0.01 butons -->
                                        <q-btn
                                            padding="1px 3px"
                                            :color="parseFloat(i) < 0 ? 'negative' : 'primary'"
                                            :label="parseFloat(i) > 0 ? '+' + i : i"
                                            @click="scope.value = fixedFloat(parseFloat(scope.value) + parseFloat(i))"
                                        />
                                    </q-item-section>
                                </q-item>
                                
                                <q-item dense>
                                    <q-item-section class="text-center">
                                        <q-btn
                                            size="sm"
                                            color="primary"
                                            label="Fill Remaining"
                                            @click="scope.value = parseFloat(parseFloat(pay.split) + parseFloat(remaining)).toFixed(2)"
                                        />
                                    </q-item-section>
                                    <q-item-section class="text-center">
                                        <q-btn
                                            outline
                                            size="sm"
                                            color="secondary"
                                            label="Close"
                                            v-close-popup
                                        />
                                    </q-item-section>
                                </q-item>
                            </template>
                        </q-popup-edit>
                    </span>
                </q-item-label>
            </q-item-section>
            <q-item-section>
                <q-item-label class="text-bold">
                    {{ parseFloat(pay.split) }}%
                </q-item-label>
            </q-item-section>
            <q-item-section side>
                <q-item-label>
                    <q-icon
                        v-if="hasIssues"
                        color="warning"
                        name="mdi-timer-alert"
                        class="cursor-pointer q-mr-sm"
                    >
                        <q-tooltip class="bg-warning text-black">
                            {{ timeError }}
                        </q-tooltip>
                    </q-icon>
                    <span class="text-underline">
                        {{ timeDiff ?? 0 }} hr{{ Math.floor(timeDiff) != 1 ? 's' : '' }}
                        <q-popup-edit
                            style="width: 400px;"
                            :model-value="timeDiff"
                            @update:model-value="setTimeDiff($event)"
                        >
                            <q-list separator>
                                <q-item>
                                    <q-item-section>
                                        <q-item-label class="text-center text-subtitle1">
                                            <q-icon 
                                                :name="`mdi-timer${timeDiff < 0 ? '-alert' : ''}-outline`"
                                                :color="timeDiff < 0 ? 'warning' : undefined"
                                            />
                                            Total: <span :class="(timeDiff < 1 ? (timeDiff == 0 ? 'white' : 'text-red') : 'text-green-5')">{{ timeDiff ?? 0 }} hr{{ Math.floor(timeDiff) != 1 ? 's' : '' }}</span>
                                        </q-item-label>
                                    </q-item-section>
                                </q-item>
                                <q-item tag="label">
                                    <q-item-section side top>
                                        <q-radio v-model="modifyPopup" val="start" />
                                    </q-item-section>

                                    <q-item-section>
                                        <q-item-label>{{  timeStart ? format(timeStart) : 'N/A' }}</q-item-label>
                                        <q-item-label caption>
                                            Clock in time
                                        </q-item-label>
                                    </q-item-section>

                                    <q-item-section v-if="pay.time_start === null" side>
                                        <q-icon name="mdi-alert" color="warning" />
                                    </q-item-section>

                                    <q-item-section side>
                                        <q-icon name="mdi-timer-play-outline" />
                                    </q-item-section>
                                </q-item>
                                <q-item tag="label">
                                    <q-item-section side top>
                                        <q-radio v-model="modifyPopup" val="end" />
                                    </q-item-section>

                                    <q-item-section>
                                        <q-item-label>{{  timeEnd ? format(timeEnd) : 'N/A' }}</q-item-label>
                                        <q-item-label caption>
                                            Clock out time
                                        </q-item-label>
                                    </q-item-section>

                                    <q-item-section v-if="pay.time_end === null" side>
                                        <q-icon name="mdi-alert" color="warning" />
                                    </q-item-section>

                                    <q-item-section side>
                                        <q-icon name="mdi-timer-stop-outline" />
                                    </q-item-section>
                                </q-item>
                            </q-list>
                            <!-- <q-item>
                                <q-item-section>
                                    <q-input
                                        label="Total Hours"
                                        :model-value="timeDiff"
                                        @update:model-value="setTimeDiff($event)"
                                        dense
                                        autofocus
                                        type="number"
                                        :rules="[val => val >= 0 || 'Time cannot be less than 0']"
                                    />
                                </q-item-section>
                            </q-item> -->
                            <q-item>
                                <q-item-section v-for="i in ['-3', '-2', '-1', '1', '2', '3']">
                                    <!-- -1, -0.1, -0.01 butons -->
                                    <q-btn
                                        padding="1px 3px"
                                        :color="parseFloat(i) < 0 ? 'negative' : 'primary'"
                                        :label="parseFloat(i) > 0 ? '+' + i : i"
                                        @click="setTimeDiff(parseFloat(i))"
                                    />
                                </q-item-section>
                            </q-item>
                            <q-item>
                                <q-item-section v-for="i in ['-0.75', '-0.5', '-0.25', '0.25', '0.5', '0.75']">
                                    <q-btn
                                        padding="1px 3px"
                                        :color="parseFloat(i) < 0 ? 'negative' : 'primary'"
                                        :label="parseFloat(i) > 0 ? '+' + i : i"
                                        @click="setTimeDiff(parseFloat(i))"
                                    />
                                </q-item-section>
                            </q-item>
                            <q-item>
                                <q-item-section>
                                    <q-btn
                                        dense
                                        no-caps
                                        label="Reset"
                                        color="negative"
                                        @click="resetTimes()"
                                    />
                                </q-item-section>
                                <q-item-section>
                                    <q-btn
                                        dense
                                        no-caps
                                        label="Close"
                                        color="primary"
                                        v-close-popup
                                    />
                                </q-item-section>
                            </q-item>
                        </q-popup-edit>
                    </span>
                </q-item-label>
            </q-item-section>
            <q-item-section side>
                <q-item-label>
                    <!-- <q-badge v-if="hasIssues">Has issues</q-badge> -->
                    <span :class="{
                        'cursor-pointer text-center q-mr-sm q-pa-xs': true,
                        'bg-grey-9': $q.dark.isActive
                    }" style="width: 120px;">
                        <q-icon name="mdi-timer-play-outline" />
                        {{ to12Hour(timeStart) }}
                        <q-tooltip>
                            Start Time {{ format(timeStart) }}
                        </q-tooltip>
                        <q-popup-proxy 
                            cover 
                            transition-show="scale" 
                            transition-hide="scale"
                        >
                            <q-time
                                now-btn
                                @update:model-value="val => saveStart(val)"
                                :model-value="timeStart"
                                :landscape="$q.screen.gt.xs"
                                mask="YYYY-MM-DD HH:mm:00"
                            >
                                <div class="row items-center justify-end">
                                    <q-btn v-close-popup label="Close" color="primary" flat />
                                </div>
                            </q-time>
                        </q-popup-proxy>
                    </span>
                    <span :class="{
                        'cursor-pointer text-center q-pa-xs': true,
                        'bg-grey-9': $q.dark.isActive
                    }" style="min-width: 72px;">
                        <q-icon name="mdi-timer-stop-outline" />
                        {{ to12Hour(timeEnd) }}
                        <q-tooltip>
                            End Time {{ format(timeEnd) }}
                        </q-tooltip>
                        <q-popup-proxy 
                            cover 
                            transition-show="scale" 
                            transition-hide="scale"
                        >
                            <q-tab-panels v-model="timeTab">
                                <q-tab-panel class="q-pa-none" name="time">
                                    <q-time
                                        now-btn
                                        :model-value="timeEnd"
                                        @update:model-value="saveEnd"
                                        :landscape="$q.screen.gt.xs"
                                        mask="YYYY-MM-DD HH:mm:00"
                                        :hour-options="visibleHours"
                                        :minute-options="visibleMinutes"
                                        @show="handleVisibility"
                                    >
                                        <div class="row items-center justify-end">
                                            <q-btn label="Change Date" @click="timeTab = 'date'" flat />
                                            <q-btn v-close-popup label="Close" color="primary" flat />
                                        </div>
                                    </q-time>
                                </q-tab-panel>
                                <q-tab-panel class="q-pa-none" name="date">
                                    <q-date
                                        :model-value="timeEnd"
                                        @update:model-value="saveEnd"
                                        :landscape="$q.screen.gt.xs"
                                        mask="YYYY-MM-DD HH:mm:00"
                                        :options="dateOptions"
                                    >
                                        <div class="row items-center justify-end">
                                            <q-btn label="Change Time" @click="timeTab = 'time'" flat />
                                            <q-btn v-close-popup label="Close" color="primary" flat />
                                        </div>
                                    </q-date>
                                </q-tab-panel>
                            </q-tab-panels>
                        </q-popup-proxy>
                    </span>
                </q-item-label>
            </q-item-section>
        </template>
        <q-list>
            <q-item>
                <q-item-section class="q-pr-xs">
                    <q-slider
                        :model-value="pay.split"
                        @update:model-value="val => { handleSlider(val); }"
                        :min="0"
                        :max="100"
                        :inner-max="parseFloat(pay.split) + parseFloat(remaining)"
                        :step="0.01"
                        color="primary"
                        label
                    />
                </q-item-section>
            </q-item>
            <q-item v-if="false">
                <q-item-section>
                    <q-item-label>
                        <!-- Date -->
                    </q-item-label>
                    <q-item-label>
                        <!-- Time -->
                    </q-item-label>
                    <q-item-label>
                        <!-- Time Zone -->
                    </q-item-label>
                </q-item-section>
                <q-item-section>
                    <q-item-label>
                        <!-- Date -->
                    </q-item-label>
                    <q-item-label>
                        <!-- Time -->
                    </q-item-label>
                    <q-item-label>
                        <!-- Time Zone -->
                    </q-item-label>
                </q-item-section>
            </q-item>
        </q-list>
    </q-expansion-item>
</template>

<script>
import Avatar from '@/Components/Avatar.vue';

export default {
    name: 'WorkerSplitRow',
    emits: ['update:pay', 'update:remaining', 'delete'],
    components: {
        Avatar
    },
    props: {
        pay: {
            type: Object,
            required: true
        },
        remaining: {
            type: Number,
            required: true
        },
        errors: {
            type: Array,
            default: () => []
        },
        workStartAt: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            timeTab: 'time', // time, date
            proxy: {
                startDate: this.formatTo('yyyy-MM-dd HH:mm:00', this.timeStart),
                endDate: this.formatTo('yyyy-MM-dd HH:mm:00', this.timeEnd),
            },
            visibleHours: [],
            visibleMinutes: [],
            invalidTimeslot: [],
            timeError: false,
            modifyPopup: 'end',
        }
    },
    methods: {
        to12Hour(time) {
            if (!time) return 'NOT SET';

            return this.formatTo('do h:mm a', time);
        },
        handleInput(percent) {
            const realRemaining = percent - this.pay.split;
            // Check if the percent is greater than the remaining
            if (realRemaining > this.remaining) {
                this.$q.notify({
                    message: `Worker split cannot be greater than ${this.remaining.toFixed(2)}%`,
                    color: 'negative',
                    icon: 'mdi-alert-circle-outline',
                    position: 'top',
                    timeout: 2000,
                });
                return;
            }
            // Handle remaining
            this.handleSlider(percent);
        },
        handleSlider(percent) {
            let temp = {...this.pay};
            
            temp.split = this.fixedFloat(percent);

            this.$emit('update:pay', temp);
        },
        addDay(date, days = 1) {
            var result = new Date(date);
            result.setDate(result.getDate() + days);
            return result;
        },
        setTimeDiff(timeDiffInt) {
            const millisecondsToAdd = Math.round(timeDiffInt * 60 * 60 * 1000);
            // Create a new Date object to avoid modifying the original date
            const newDate = new Date(this.modifyPopup == 'end' ? this.timeEnd : this.timeStart);
            // Add the milliseconds to the new date
            newDate.setTime(newDate.getTime() + millisecondsToAdd);
            
            newDate.setSeconds(0);

            if (this.modifyPopup == 'end')  {
                this.saveEnd(this.formatTo('yyyy-MM-dd HH:mm:00', newDate));
            } else {
                this.saveStart(this.formatTo('yyyy-MM-dd HH:mm:00', newDate));
            }
        },
        resetTimes() {
            const time_start = this.formatTo('yyyy-MM-dd HH:mm:00', this.workStartAt);
            const time_end = this.formatTo('yyyy-MM-dd HH:mm:00', this.workStartAt);

            this.$emit('update:pay', Object.assign({
                ...this.pay
            }, {
                time_start,
                time_end
            }));
            
            this.proxy.startDate = time_start;
            this.proxy.endDate = time_end;
        },
        saveStart(val) {
            this.proxy.startDate = val;

            this.$emit('update:pay', Object.assign({
                ...this.pay
            }, {
                time_start: val,
                time_end: this.timeEnd
            }));

            this.handleVisibility();
        },
        saveEnd(val) {
            this.proxy.endDate = val;

            this.$emit('update:pay', Object.assign({
                ...this.pay
            }, {
                time_end: val,
                time_start: this.timeStart
            }));
        },
        handleVisibility() {
            let [startHr, startMin] = this.proxy.startDate.split(' ')[1].split(':');
            let [endHr, endMin] = this.proxy.endDate.split(' ')[1].split(':');

            let hours = parseInt(startHr);

            this.visibleHours = [];
            this.visibleMinutes = [];

            if (this.proxy.startDate !== this.proxy.endDate) {
                hours = 0;
            }

            if (parseInt(startHr) === parseInt(endHr)) {
                for (let i = parseInt(startMin) + 1; i < 60; i++) {
                    this.visibleMinutes.push(parseInt(i));
                }
            }
            
            for (let i = hours; i <= 24; i++) {
                this.visibleHours.push(parseInt(i));
            }

            if (!this.visibleHours.length) this.visibleHours = undefined;
            if (!this.visibleMinutes.length) this.visibleMinutes = undefined;
        },
    },
    computed: {
        timeStart() {
            let start = this.pay.time_start;

            if (start === null || start === undefined) {
                start = this.workStartAt;
            }

            return this.formatTo('yyyy-MM-dd HH:mm:00', start);
        },
        timeEnd() {
            let end = this.pay.time_end;

            if (end === null || end === undefined) {
                end = this.workStartAt;
            }

            return this.formatTo('yyyy-MM-dd HH:mm:00', end);
        },
        dateOptions() {
            // Return work start date and plus 1 day YYYY-MM-DD
            return [
                this.formatTo('yyyy/MM/dd', this.timeStart),
                this.formatTo('yyyy/MM/dd', this.addDay(this.timeStart))
            ];
        },
        timeDiff() {
            const start = new Date(this.timeStart);
            start.setSeconds(0);
            start.setMilliseconds(0);

            const end = new Date(this.timeEnd);
            end.setSeconds(0);
            end.setMilliseconds(0);
            
            // Compute the difference in milliseconds
            const diffMillis = end.getTime() - start.getTime();

            // Convert to hours with explicit rounding
            const diffInHours = Math.round((diffMillis / 1000 / 60 / 60) * 100) / 100;

            return diffInHours.toFixed(2); // Return as a string with 2 decimal places
        },
        hasIssues() {
            if (
                parseFloat(this.timeDiff) <= 0 || 
                this.pay.time_start === null || 
                this.pay.time_end === null ||
                // Check if the dates are the same
                this.pay.time_start === this.pay.time_end
            ) {
                this.timeError = 'Time clock needs to be set';

                return true;
            }
            // Check if the time start is greater than time end
            if (this.pay.time_start > this.pay.time_end) {
                this.timeError = 'Time start cannot be greater than time end';
                return true;
            }

            this.timeError = false;
            
            return false;
        }
    },
    mounted() {
        this.handleVisibility();
    },
    watch: {
        'proxy.startDate': 'handleVisibility',
        'proxy.endDate': 'handleVisibility',
    }
}
</script>